import { useQuery } from '@apollo/client'
import Badge from '@components/Badge/Badge.component'
import FullScreenLoader from '@components/FullScreenLoader/FullScreenLoader.component'
import { Lot } from '@customTypes/lot.interface'
import { Subscription, SubscriptionStatus } from '@customTypes/subscription.interface'
import { GET_LOT_WITH_ID } from '@graphql/queries/getLotWithId.query'
import { GET_USER_SUBSCRIPTIONS } from '@graphql/queries/getUserSubscriptions'
import { useAuthenticatedQuery } from '@hooks/useAuth'
import { calculateVAT } from '@utils/calculateVAT.utils'
import { formatPriceWithCurrency } from '@utils/formatPriceWithCurrency.utils'
import { getSubscriptionDurationStatus } from '@utils/getDurationStatus.utils'
import { priceToString } from '@utils/priceToString.utils'
import LotInfo from '@views/Checkout/components/LotInfo/LotInfo.component'
import { DateTime } from 'luxon'
import { BookingGetLotQuery, BookingGetLotQueryVariables } from 'parkdepot-shared/gql/graphql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import DeleteSubscription from './components/DeleteSubscription/DeleteSubscription.component'
import {
    Container,
    ContentContainer,
    Description,
    DescriptionTitle,
    DetailsLine,
    Line,
    PriceText,
    RightContainer,
    SectionTitle,
    Subtitle,
    Text,
    Title,
    VatText,
    WarningTextContainer
} from './SubscriptionDetails.style'

const SubscriptionDetails = () => {
    const { orderId } = useParams<{ orderId: string }>()
    const { t, i18n } = useTranslation()

    const { data: subscriptionsFromApi, loading } = useAuthenticatedQuery<{
        get_user_subscriptions: Subscription[]
    }>({
        query: GET_USER_SUBSCRIPTIONS,
        fetchPolicy: 'cache-first'
    })

    const subs = subscriptionsFromApi?.get_user_subscriptions || []
    const subscription = subs.find((sub) => sub.id === orderId)
    const status = getSubscriptionDurationStatus(subscription?.start_date || '', subscription?.end_date || null)
    // subscription is used if start date is in the past
    const subUsed = subscription?.start_date ? DateTime.fromISO(subscription.start_date) < DateTime.now() : false

    const { data: lotData, loading: lotDataLoading } = useQuery<BookingGetLotQuery, BookingGetLotQueryVariables>(
        GET_LOT_WITH_ID,
        {
            skip: !subscription,
            variables: subscription?.lot_id ? { lot_id: subscription.lot_id } : undefined
        }
    )

    const lot = lotData?.booking_get_lot
    const vatData = calculateVAT(Number(subscription?.subscription_price || 0), lot?.lot_country_code || 'DE')

    const description = lot?.description
        ? (lot.description as any)[i18n.language.split('-')[0]?.toLowerCase() || 'de']
        : ''

    return (
        <Container>
            {loading && <FullScreenLoader />}
            {!loading && (
                <>
                    <ContentContainer>
                        <Title>{t('checkout.subscriptions.details')}</Title>
                        <Subtitle>{t('checkout.success.details')}</Subtitle>
                        <DetailsLine>
                            <Text>{t('checkout.success.status')}</Text>
                            <Text $right>
                                <Badge variant={status.variant} text={t(status.tKey)} />
                            </Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.vehicle')}</Text>
                            <RightContainer>
                                {subscription?.plates?.map((plate) => (
                                    <Text $right key={plate}>
                                        {plate}
                                    </Text>
                                ))}
                            </RightContainer>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.email')}</Text>
                            <Text $right>{subscription?.user_email}</Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.lenght')}</Text>
                            <Text $right>{`${t('subscriptions.duration')}`}</Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.validFrom')}</Text>
                            <Text $right>
                                {DateTime.fromISO(subscription?.start_date as string).toFormat('dd.MM.yyyy')}
                            </Text>
                        </DetailsLine>
                        {/* <DetailsLine>
                    <Text>{t('checkout.success.paymentMethod')}</Text>
                    <Text $right>{subscription?.payment_method}</Text>
                </DetailsLine> */}
                        <DetailsLine>
                            <Text $bold>{t('checkout.success.price')}</Text>
                            <RightContainer>
                                <PriceText>
                                    {formatPriceWithCurrency().format(
                                        priceToString(subscription?.subscription_price || 0)
                                    )}
                                </PriceText>
                                {!lotDataLoading && (
                                    <VatText>
                                        {t('checkout.success.included')} {vatData.percentage}%{' '}
                                        {t('checkout.success.vat')}{' '}
                                        {formatPriceWithCurrency().format(priceToString(vatData.value))}
                                    </VatText>
                                )}
                            </RightContainer>
                        </DetailsLine>
                        {status.variant === 'information' && (
                            <WarningTextContainer>
                                <i className="wemolo-warning-circle" />
                                <div>{t('subscriptions.bookingDetails.warningText')}</div>
                            </WarningTextContainer>
                        )}
                    </ContentContainer>
                    <Line />
                    <ContentContainer>
                        <SectionTitle>{t('checkout.success.parkingInformation')}</SectionTitle>
                        <LotInfo lot={lot as unknown as Lot} boldedTitles column />
                        <DescriptionTitle>{t('checkout.success.description')}</DescriptionTitle>
                        <Description dangerouslySetInnerHTML={{ __html: description }} />
                    </ContentContainer>
                    {(subscription?.subscription_status === SubscriptionStatus.ACTIVE ||
                        subscription?.subscription_status === SubscriptionStatus.FUTURE) && (
                        <DeleteSubscription subId={subscription?.id || ''} subscriptionUsed={subUsed} />
                    )}
                </>
            )}
        </Container>
    )
}

export default SubscriptionDetails
